/**
 * "Try New requests" — the per-person switch for the New requests worklist
 * (Corbin approved 2026-10-28, mockup https://claude.ai/artifact/98SqJMh6FSCfJHEvxopBQT).
 *
 * Same server-backed store as "Try the new Grid" (`user_preferences`,
 * /api/me/preferences), so it follows a person from desk to laptop and needs
 * no migration. OFF by default: Darcy pilots it first. With it off, the
 * booking chip, its flyout and the Sales Pipeline work exactly as before.
 *
 * With it on: the toolbar shows the New requests chip (New + Trying to
 * reach) instead of the booking chip, and a tap opens /requests, the one
 * list. The Grid's signpost counts the same list and links there too.
 */
import { toast } from "sonner";
import { useUserPreference } from "@/lib/user-preference";

export const NEW_REQUESTS_PREF_KEY = "requests.new-requests";

export function useNewRequestsSwitch(): { on: boolean; set: (on: boolean) => void; loaded: boolean } {
    const [value, set, { loaded }] = useUserPreference<boolean>(NEW_REQUESTS_PREF_KEY, false, {
        onError: () => toast.error("Couldn't save the New requests switch. It's back the way it was."),
    });
    return { on: value === true, set, loaded };
}

/** The score the server sends for one request (services/requests/callTries.ts). */
export interface TriesSummary {
    count: number;
    calls: number;
    messages: number;
    lastAt: string | null;
    lastOutcome: TryOutcome | null;
    line: string;
    nextTryAt: string | null;
    next: "call" | "message" | "cant_reach" | "wait" | "fix_phone" | "booked";
    nextLine: string;
    offerMessage: boolean;
    offerCantReach: boolean;
}

export type CallOutcome = "no_answer" | "voicemail" | "talked_booked" | "talked_not_now" | "wrong_number";
export type TryOutcome = CallOutcome | "texted" | "emailed";

export const CALL_BUTTONS: ReadonlyArray<{ outcome: CallOutcome; label: string }> = [
    { outcome: "no_answer", label: "No answer" },
    { outcome: "voicemail", label: "Left voicemail" },
    { outcome: "talked_booked", label: "Talked, booked" },
    { outcome: "talked_not_now", label: "Talked, not now" },
    { outcome: "wrong_number", label: "Wrong number" },
];

export const CALL_BACK_CHOICES: ReadonlyArray<{ days: number; label: string }> = [
    { days: 7, label: "Next week" },
    { days: 14, label: "In 2 weeks" },
    { days: 30, label: "Next month" },
];
